import { CARD_SIZES, CardSize } from '@blisspointmedia/bpm-types/dist/dashboarding';
import { CardType, CardTypeDefinition } from './dashboard-types';

// Twelve, not four, because a third of the row has to be a whole number of columns. Twelve also
// divides by 2, 3, 4 and 6, which is what makes `one-third` (4) and `two-thirds` (8) expressible at all
// -- and leaves a future quarter-width preset needing no grid change either.
export const GRID_COLUMNS = 12;
export const GRID_GAP = 24; // px

// The grid's row unit — deliberately far smaller than a card. When the unit WAS the card height a
// card could only be 540 / 1104 / 1668px tall; separating them makes height a free dial. Fed to the
// CSS as --dashboard-row-unit (DashboardGrid.tsx) so there is one number, not two kept in sync.
export const ROW_UNIT = 36; // px

// One row's pitch: the unit plus the gap that follows it. A card spanning N rows is
// N * ROW_PITCH - GRID_GAP tall, so heights step in 60px — finer than anyone can tune by eye.
export const ROW_PITCH = ROW_UNIT + GRID_GAP;

// Drag auto-scroll: within AUTO_SCROLL_EDGE px of the canvas's top or bottom, scroll at up to
// AUTO_SCROLL_MAX_SPEED. Per second, not per frame, so 120Hz screens don't scroll twice as fast.
export const AUTO_SCROLL_EDGE = 40; // px
export const AUTO_SCROLL_MAX_SPEED = 800; // px per second

// A preset's shape: `width` in grid columns, `heightPx` in pixels. Heights are written in pixels and
// the row span derived, so retuning a size means editing one readable number and reloading.
interface CardSizeSpec {
  width: number;
  heightPx: number;
}

// The four presets. `one-third` is a third of the row, `standard` a half, `two-thirds` two thirds,
// `wide` the whole thing -- 4, 6, 8 and 12 of the twelve columns, each dividing the grid exactly.
// `one-third` is 5 rows tall while `standard`, `two-thirds` and `wide` are 10 -- half height, not the
// same height. 276 was chosen (not just any legal row count) because two `one-third`s stacked, gap
// included, land exactly on one `standard`'s bottom edge: 276 + 24 + 276 = 576. A row of thirds
// packs flush against whatever comes next instead of leaving a drifting half-row gap. `two-thirds`
// exists specifically so `one-third + two-thirds` (4 + 8 = 12) fills a row exactly, unlike
// `one-third + standard` (4 + 6 = 10), which strands two columns. Growing this list is additive -- add
// the name in bpm-types' CARD_SIZES, its shape here, and a rule for it in
// CARD_SIZING_BY_CHART_TYPE.
//
// WARNING Every heightPx MUST be a whole number of rows (`N * ROW_PITCH - GRID_GAP`). Anything else
// would round in `cardRowSpan` and leave the card a few pixels out of line with its neighbours
// forever, so it throws instead.
export const SIZE_DIMENSIONS: Record<CardSize, CardSizeSpec> = {
  'one-third': { width: 4, heightPx: 276 },
  standard: { width: 6, heightPx: 576 },
  'two-thirds': { width: 8, heightPx: 576 },
  wide: { width: 12, heightPx: 576 }
};

export const DEFAULT_CARD_SIZE: CardSize = 'standard';

export const CARD_TYPES: CardTypeDefinition[] = [
  {
    id: CardType.CHART,
    name: 'Chart',
    defaultSize: DEFAULT_CARD_SIZE,
    availableSizes: CARD_SIZES
  }
];
